import PropTypes from 'prop-types';
import { BsJournalText } from 'react-icons/bs';

import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

import { useAgentRunActions } from '../models/queries';
import { toolDataRows } from '../utils/toolDataRows';
import { toolErrorText } from '../utils/toolErrors';
import { TOOL_NAMES } from '../utils/toolLabels';

const fmt = (value) => (value ?? 0).toLocaleString('de-DE');
const STATUS_LABELS = { ok: 'OK', error: 'Fehler', pending: 'Offen' };

/**
 * The audit of a conversation: every tool call with its (masked) input, outcome, credits,
 * vendor cost and duration.
 */
const ProtocolModal = ({ runId, isOpen, onClose }) => {
	const { data: actions, isPending } = useAgentRunActions(runId, isOpen);
	const credits = (actions ?? []).reduce((sum, action) => sum + (action.creditsCharged ?? 0), 0);
	const vendor = (actions ?? []).reduce((sum, action) => sum + (action.vendorCostUsd ?? 0), 0);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="l">
			<Panel.Header
				icon={BsJournalText}
				onClose={onClose}
				subtitle={`${fmt(credits)} Credits abgebucht${vendor > 0 ? ` · Anbieterkosten ${vendor.toFixed(2)} USD` : ''}`}
			>
				Protokoll des Assistenten
			</Panel.Header>
			<Panel.Body>
				{isPending && <p className="text-sm text-bgBase-800">Lädt …</p>}
				{!isPending && (actions ?? []).length === 0 && (
					<p className="text-sm text-bgBase-800">Noch keine Schritte.</p>
				)}
				<ol className="space-y-2">
					{(actions ?? []).map((action) => (
						<li key={action.id} className="rounded-card border border-bgBase-500 p-3 text-sm">
							<div className="flex items-center gap-2">
								<span className="text-xs text-bgBase-800">Schritt {action.step}</span>
								<span className="font-medium">{TOOL_NAMES[action.tool] ?? action.tool}</span>
								<span className="ml-auto text-xs text-bgBase-800">
									{STATUS_LABELS[action.status] ?? action.status}
									{action.creditsCharged ? ` · ${fmt(action.creditsCharged)} Credits` : ''}
									{action.durationMs ? ` · ${(action.durationMs / 1000).toFixed(1)} s` : ''}
								</span>
							</div>
							{action.error && (
								<p className="mt-1 text-xs text-warning-700">{toolErrorText(action.error)}</p>
							)}
							{action.input && toolDataRows(action.input).length > 0 && (
								<div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
									{toolDataRows(action.input).map((row) => (
										<span key={row.label} className="text-xs text-bgBase-800">
											{row.label}: <span className="text-text">{row.value}</span>
										</span>
									))}
								</div>
							)}
						</li>
					))}
				</ol>
			</Panel.Body>
		</Modal>
	);
};

ProtocolModal.propTypes = {
	runId: PropTypes.string,
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default ProtocolModal;
